import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { get, post, put, del } from '../utils/api';
import { useSuccessSnack, useErrorSnack } from '../utils/snackbar';
import i18n from '../i18n';

export async function getOrganization({ id }) {
  const res = await get(`/organization/${id}`);
  return res?.data;
}

export async function getOrganizations() {
  const res = await get('/organization');
  return res?.data;
}

export async function createOrganization(organization) {
  const res = await post('/organization', organization);
  return res?.data;
}

export async function fetchCustomerPortalUrl({ id }) {
  const res = await post(`/organization/${id}/stripe-customer-portal`);
  return res?.data;
}
export async function updateOrganization({ name, users, meetingBotSettings }) {
  const res = await put(`/organization`, {
    name,
    users,
    meeting_bot_settings: meetingBotSettings,
  });
  return res?.data;
}

export async function updateOrganizationAdmin(
  id,
  {
    name,
    users,
    speakerIdentification,
    type,
    languages,
    seatLimit,
    outputFormat,
    allowGpt,
    allowPhone,
    allowBankID,
    jobPriority,
    jobConcurrencyLimit,
    apiAccess,
    credits,
    uploadRateLimit,
    meetingBotSettings,
    hideSpeakers,
    hideSuggestedEdits,
    secureLogin,
    allowMultilang,
    analysisMode,
    trialMode,
    trialEndDate,
    meetingMinutes,
    customBotSettings,
    invites,
    allowHybridMeeting,
  }
) {
  const res = await put(`/organization/${id}`, {
    name,
    users,
    speaker_identification: speakerIdentification,
    languages,
    type,
    seat_limit: seatLimit,
    output_format: outputFormat,
    allow_gpt: allowGpt,
    allow_phone: allowPhone,
    allow_bank_id: allowBankID,
    job_priority: jobPriority,
    job_concurrency_limit: jobConcurrencyLimit,
    api_access: apiAccess,
    credits,
    upload_rate_limit: uploadRateLimit,
    meeting_bot_settings: meetingBotSettings,
    hide_speakers: hideSpeakers,
    hide_suggested_edits: hideSuggestedEdits,
    secure_login: secureLogin,
    allow_multilang: allowMultilang,
    analysis_mode: analysisMode,
    trial_mode: trialMode,
    trial_end_date: trialEndDate,
    meeting_minutes: meetingMinutes,
    custom_bot_settings: customBotSettings,
    invites,
    allow_hybrid_meeting: allowHybridMeeting,
  });
  return res?.data;
}

async function updateOrganizationMeetingBotSettingsImage(
  id,
  { form, onProgress }
) {
  const res = await post(`/organization/${id}/meeting-bot-image`, form, {
    onUploadProgress: onProgress,
  });
  return res?.data;
}

async function updateOrganizationPlanQuantity(id, { quantity }) {
  const res = await put(`/organization/${id}/plan-quantity`, { quantity });
  return res?.data;
}

async function addModule({ priceId }) {
  const res = await post(`/organization/subscription/modules`, { priceId });

  return res?.data;
}

async function cancelModule(priceId) {
  const res = await del(`/organization/subscription/modules/${priceId}`);
  return res?.data;
}

async function addModuleAdmin(id, { moduleId }) {
  const res = await post(`/organization/${id}/modules`, { moduleId });
  return res?.data;
}

async function removeModuleAdmin(id, { moduleId }) {
  const res = await del(`/organization/${id}/modules/${moduleId}`);
  return res?.data;
}

export async function createCheckoutSession({ priceId, quantity }) {
  const res = await post(`/organization/create-checkout-session`, {
    priceId,
    quantity,
  });
  return res?.data;
}

async function inviteUser(id, { email }) {
  const res = await post(`/organization/${id}/invite`, { email });
  return res?.data;
}

async function updateUser(id, { userId, ...data }) {
  const res = await put(`/organization/${id}/user/${userId}`, data);
  return res?.data;
}

async function getOrganizationBilling(id) {
  const res = await get(`/organization/${id}/billing`);
  return res?.data;
}

export async function getOrganizationAssets(id, folderPrefix) {
  const res = await get(`/organization/${id}/assets`, {
    params: { folder_prefix: folderPrefix },
  });
  return res?.data;
}

export async function getLatestOrganizationAssets(id) {
  const res = await get(`/organization/${id}/assets/latest`);
  return res?.data;
}

async function createTranscribeMode(id, { name }) {
  const res = await post(`/organization/${id}/transcribe-mode`, { name });
  return res?.data;
}

async function createNameList(id, { name }) {
  const res = await post(`/organization/${id}/name-list`, { name });
  return res?.data;
}

async function removeUser(id, userId) {
  const res = await del(`/organization/${id}/user/${userId}`);
  return res?.data;
}

async function createTemplate(orgId, { name, data }) {
  const res = await post(`/organization/${orgId}/templates`, { name, data });
  return res?.data;
}

async function updateTemplate(orgId, { id, name, status, data }) {
  const res = await put(`/organization/${orgId}/templates/${id}`, {
    name,
    status,
    data,
  });
  return res?.data;
}

async function deleteTemplate(orgId, { id }) {
  const res = await del(`/organization/${orgId}/templates/${id}`);
  return res?.data;
}

async function uploadTemplateFile(orgId, { id, file }) {
  const res = await post(`/organization/${orgId}/templates/${id}/upload`, file);
  return res?.data;
}

async function getMeetingBotSettingsImage(id, { width, height, fit }) {
  try {
    const res = await get(`/organization/${id}/meeting-bot-image`, {
      params: { width, height, fit },
      responseType: 'blob',
    });

    if (!res.data.size) return null;

    const url = window.URL.createObjectURL(new Blob([res?.data]));
    return url;
  } catch (err) {
    return null;
  }
}

async function createFlow(orgId, { name }) {
  const res = await post(`/organization/${orgId}/flows`, { name });
  return res?.data;
}

export function useMeetingBotSettingsImageQuery(
  id,
  { enabled, width, height, fit }
) {
  const { data, isLoading, error } = useQuery(
    ['organization', id, width],
    () => getMeetingBotSettingsImage(id, { width, height, fit }),
    {
      enabled,
      cacheTime: 'Infinity',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      retry: false,
    }
  );

  return { image: data, isLoading, error };
}

export function useWorkspaceQuery({ id }) {
  const { data: workspace } = useQuery(
    ['workspace', parseInt(id, 10)],
    () => getOrganization({ id }),
    {
      enabled: !!id,
    }
  );

  const { data: billingData } = useQuery(
    ['workspace_billing', parseInt(id, 10)],
    () => getOrganizationBilling(id),
    {
      enabled: !!id,
    }
  );

  const { billing, events, monthUsage } = useMemo(() => {
    const { events, billing, month_usage: monthUsage } = billingData || {};
    return { billing, events, monthUsage };
  }, [billingData]);

  return {
    workspace,
    users: workspace?.users,
    billing,
    billingEvents: events,
    monthUsage,
  };
}

export function useWorkspaceActions({ id } = {}) {
  const queryClient = useQueryClient();

  const {
    mutate: _inviteUser,
    mutateAsync: inviteUserAsync,
    isSuccess: isInviteUserSuccess,
    isError: isInviteUserError,
  } = useMutation((data) => inviteUser(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
      queryClient.invalidateQueries('self');
    },
  });

  useSuccessSnack(i18n.t('Användare inbjuden'), isInviteUserSuccess);
  useErrorSnack(i18n.t('Kunde inte bjuda in användare'), isInviteUserError);

  const {
    mutate: _updateUser,
    mutateAsync: updateUserAsync,
    isSuccess: isUpdateUserSuccess,
    isError: isUpdateUserError,
  } = useMutation((data) => updateUser(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack(i18n.t('Användare uppdaterad'), isUpdateUserSuccess);
  useErrorSnack(i18n.t('Kunde inte uppdatera användare'), isUpdateUserError);

  const {
    mutate: _updateWorkspace,
    isSuccess: isUpdateOrganizationSuccess,
    isError: isUpdateOrganizationError,
  } = useMutation((data) => updateOrganization(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
      queryClient.invalidateQueries(['workspace_billing', id]);
      queryClient.invalidateQueries('organization');
      queryClient.invalidateQueries('self');
    },
  });

  const { mutate: _updateWorkspaceMeetingBotSettingsImage } = useMutation(
    (data) => updateOrganizationMeetingBotSettingsImage(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workspace', id]);
        queryClient.invalidateQueries('organization');
        queryClient.invalidateQueries('self');
      },
    }
  );

  const {
    mutate: _updatePlanQuantity,
    mutateAsync: updatePlanQuantityAsync,
    isSuccess: isUpdatePlanQuantitySuccess,
    isError: isUpdatePlanQuantityError,
  } = useMutation((data) => updateOrganizationPlanQuantity(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('credits');
      queryClient.invalidateQueries(['workspace', id]);
      queryClient.invalidateQueries(['workspace_billing', id]);
      queryClient.invalidateQueries('organization');
      queryClient.invalidateQueries('self');
    },
  });

  const {
    mutate: buyModule,
    mutateAsync: buyModuleAsync,
    isSuccess: isBuyModuleSuccess,
    isError: isBuyModuleError,
    isLoading: isBuyModuleLoading,
  } = useMutation((priceId) => addModule(priceId), {
    onSuccess: () => {
      queryClient.invalidateQueries('credits');
      queryClient.invalidateQueries(['workspace', id]);
      queryClient.invalidateQueries(['workspace_billing', id]);
      queryClient.invalidateQueries('organization');
      queryClient.invalidateQueries('self');
    },
  });

  useSuccessSnack(i18n.t('Abonnemang uppdaterat'), isUpdatePlanQuantitySuccess);
  useErrorSnack(
    i18n.t('Kunde inte uppdatera abonnemang'),
    isUpdatePlanQuantityError
  );

  const {
    mutate: _cancelModule,
    mutateAsync: cancelModuleAsync,
    isSuccess: isCancelModuleSuccess,
    isError: isCancelModuleError,
  } = useMutation((priceId) => cancelModule(priceId), {
    onSuccess: () => {
      queryClient.invalidateQueries('credits');
      queryClient.invalidateQueries(['workspace', id]);
      queryClient.invalidateQueries(['workspace_billing', id]);
      queryClient.invalidateQueries('organization');
      queryClient.invalidateQueries('self');
    },
  });

  useSuccessSnack(i18n.t('Modul avslutad'), isCancelModuleSuccess);
  useErrorSnack(i18n.t('Kunde inte avsluta modul'), isCancelModuleError);

  useSuccessSnack(i18n.t('Arbetsyta updaterad'), isUpdateOrganizationSuccess);
  useErrorSnack(
    i18n.t('Kunde inte uppdatera arbetsyta'),
    isUpdateOrganizationError
  );

  const {
    mutate: _removeUser,
    mutateAsync: removeUserAsync,
    isSuccess: isRemoveUserSuccess,
    isError: isRemoveUserError,
  } = useMutation((userId) => removeUser(id, userId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack(i18n.t('Användare borttagen'), isRemoveUserSuccess);
  useErrorSnack(i18n.t('Kunde inte ta bort användare'), isRemoveUserError);

  return {
    inviteUser: _inviteUser,
    inviteUserAsync,
    updateUser: _updateUser,
    updateUserAsync,
    updateWorkspace: _updateWorkspace,
    updateWorkspaceMeetingBotSettingsImage:
      _updateWorkspaceMeetingBotSettingsImage,
    removeUser: _removeUser,
    removeUserAsync,
    updatePlanQuantity: _updatePlanQuantity,
    updatePlanQuantityAsync,
    buyModule,
    buyModuleAsync,
    isBuyModuleSuccess,
    isBuyModuleLoading,
    isBuyModuleError,
    cancelModule: _cancelModule,
    cancelModuleAsync,
  };
}

export function useWorkspaceAdminActions({ id } = {}) {
  const queryClient = useQueryClient();

  const {
    mutate: _createWorkspace,
    isLoading: isCreatingOrganization,
    isSuccess: isCreateOrganizationSuccess,
  } = useMutation((organization) => createOrganization(organization), {
    onSuccess: () => {
      queryClient.invalidateQueries('organizations');
    },
  });

  const {
    mutate: _updateWorkspace,
    mutateAsync: updateWorkspaceAsync,
    isLoading: isUpdatingOrganization,
    isSuccess: isUpdateOrganizationSuccess,
    error: updateOrganizationError,
    isError: isUpdateOrganizationError,
  } = useMutation((data) => updateOrganizationAdmin(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('organizations');
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack('Organization created', isCreateOrganizationSuccess);
  useSuccessSnack('Organization updated', isUpdateOrganizationSuccess);

  let updateErrorMessage = i18n.t('Fel uppstod vid uppdatering av arbetsyta');
  if (updateOrganizationError) {
    if (updateOrganizationError.response.status === 400) {
      updateErrorMessage = updateOrganizationError.response.data.error;
    }
  }
  useErrorSnack(updateErrorMessage, isUpdateOrganizationError);

  const {
    mutate: _createTranscribeMode,
    mutateAsync: createTranscribeModeAsync,
    isSuccess: isCreateTranscribeModeSuccess,
    isError: isCreateTranscribeModeError,
  } = useMutation((data) => createTranscribeMode(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack('Transcribe mode created', isCreateTranscribeModeSuccess);
  useErrorSnack(
    'Could not create transcribe mode',
    isCreateTranscribeModeError
  );

  const {
    mutate: _createNameList,
    mutateAsync: createNameListAsync,
    isSuccess: isCreateNameListSuccess,
    isError: isCreateNameListError,
  } = useMutation((data) => createNameList(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack('Name list created', isCreateNameListSuccess);
  useErrorSnack('Could not create name list', isCreateNameListError);

  const {
    mutate: _createTemplate,
    mutateAsync: createTemplateAsync,
    isSuccess: isCreateTemplateSuccess,
    isError: isCreateTemplateError,
  } = useMutation((data) => createTemplate(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useErrorSnack('Could not create template', isCreateTemplateError);

  const {
    mutate: _updateTemplate,
    mutateAsync: updateTemplateAsync,
    isSuccess: isUpdateTemplateSuccess,
    isError: isUpdateTemplateError,
  } = useMutation((data) => updateTemplate(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack('Template updated', isUpdateTemplateSuccess);
  useErrorSnack('Could not update template', isUpdateTemplateError);

  const {
    mutate: _deleteTemplate,
    mutateAsync: deleteTemplateAsync,
    isSuccess: isDeleteTemplateSuccess,
    isError: isDeleteTemplateError,
  } = useMutation((data) => deleteTemplate(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  const {
    mutate: _uploadTemplateFile,
    mutateAsync: uploadTemplateFileAsync,
    isSuccess: isUploadTemplateFileSuccess,
    isError: isUploadTemplateFileError,
  } = useMutation((data) => uploadTemplateFile(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  // Flow
  const {
    mutateAsync: createFlowAsync,
    isSuccess: isCreateFlowSuccess,
    isError: isCreateFlowError,
  } = useMutation((data) => createFlow(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack('Flow created', isCreateFlowSuccess);
  useErrorSnack('Could not create flow', isCreateFlowError);

  const {
    mutate: _addModuleAdmin,
    mutateAsync: addModuleAdminAsync,
    isSuccess: isAddModuleAdminSuccess,
    isError: isAddModuleAdminError,
  } = useMutation((moduleId) => addModuleAdmin(id, { moduleId }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack('Module added', isAddModuleAdminSuccess);
  useErrorSnack('Could not add module', isAddModuleAdminError);

  const {
    mutate: _removeModuleAdmin,
    mutateAsync: removeModuleAdminAsync,
    isSuccess: isRemoveModuleAdminSuccess,
    isError: isRemoveModuleAdminError,
  } = useMutation((moduleId) => removeModuleAdmin(id, { moduleId }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace', id]);
    },
  });

  useSuccessSnack('Module removed', isRemoveModuleAdminSuccess);
  useErrorSnack('Could not remove module', isRemoveModuleAdminError);

  return {
    createWorkspace: _createWorkspace,
    updateWorkspace: _updateWorkspace,
    updateWorkspaceAsync,
    updateWorkspaceMeetingBotSettingsImage: _updateWorkspace,
    isUpdateOrganizationSuccess,
    isCreateOrganizationSuccess,
    isUpdatingOrganization,
    isCreatingOrganization,
    createTranscribeMode: _createTranscribeMode,
    createTranscribeModeAsync,
    createNameList: _createNameList,
    createNameListAsync,
    createTemplate: _createTemplate,
    createTemplateAsync,
    updateTemplate: _updateTemplate,
    updateTemplateAsync,
    uploadTemplateFile: _uploadTemplateFile,
    uploadTemplateFileAsync,
    deleteTemplate: _deleteTemplate,
    deleteTemplateAsync,
    createFlow: createFlowAsync,
    addModuleAsync: addModuleAdminAsync,
    removeModuleAsync: removeModuleAdminAsync,
  };
}

export function useWorkspacesQuery() {
  const { data } = useQuery('organizations', getOrganizations);
  const workspaces = useMemo(() => {
    return data || [];
  }, [data]);

  return { workspaces };
}

export function useWorkspaceAssetsQuery({ id, folderPrefix, latest }) {
  const { data: _assets, isLoading: isLoadingAssets } = useQuery(
    ['organization', id, 'assets', folderPrefix],
    () => getOrganizationAssets(id, folderPrefix),
    {
      enabled: !!id && !!folderPrefix,
    }
  );

  const assets = useMemo(
    () => _assets || { pending: [], ready: [] },
    [_assets]
  );

  const {
    data: latestAssets,
    isLoading: isLoadingLatestAssets,
    isFetching: isFetchingLatestAssets,
  } = useQuery(
    ['organization', id, 'assets_latest'],
    () => getLatestOrganizationAssets(id),
    {
      enabled: !!id && latest,
      refetchInterval: (data) => {
        const inQueue = data?.latest?.filter(
          ({ status: x }) => x !== 'ready' && x !== 'error'
        )?.length;
        return inQueue ? 5000 : false;
      },
    }
  );

  return {
    assets,
    isLoadingAssets,
    latestAssets: latestAssets?.latest || [],
    latestAssetsGrouped: latestAssets?.grouped || {},
    isLoadingLatestAssets,
    isFetchingLatestAssets,
  };
}

async function getPricing() {
  const res = await get('/organization/pricing');
  return res?.data;
}

async function getModules() {
  const res = await get('/organization/modules');
  return res?.data;
}

async function getRateLimits() {
  const res = await get('/organization/rate-limits');
  return res?.data;
}

export function useRateLimitsQuery({ enabled = true } = {}) {
  const { data } = useQuery('rate_limits', getRateLimits, {
    enabled,
  });

  return { rateLimits: data };
}

export function usePricingQuery({ enabled = true } = {}) {
  const {
    data: pricing,
    isLoading: isLoadingPricing,
    isFetching: isFetchingPricing,
  } = useQuery('pricing', getPricing, {
    enabled,
  });

  const {
    data: modules,
    isLoading: isLoadingModules,
    isFetching: isFetchingModules,
  } = useQuery('modules', getModules, {
    enabled,
  });

  return {
    pricing,
    modules,
    isLoading: isLoadingPricing || isLoadingModules,
    isFetching: isFetchingPricing || isFetchingModules,
  };
}
